import { Check, X } from "lucide-react";
import { checkPasswordStrength } from "@/lib/validations/auth";

interface PasswordStrengthIndicatorProps {
  password: string;
}

export const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  if (!password) return null;

  const { checks, strength } = checkPasswordStrength(password);

  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500'
  };

  const strengthTextColors = {
    weak: 'text-red-600',
    medium: 'text-yellow-600',
    strong: 'text-green-600'
  };

  return (
    <div className="space-y-2 mt-2">
      {/* Strength bar */}
      <div className="flex gap-1">
        <div className={`h-1 flex-1 rounded ${strength === 'weak' ? strengthColors.weak : 'bg-gray-200'}`} />
        <div className={`h-1 flex-1 rounded ${strength === 'medium' || strength === 'strong' ? strengthColors[strength] : 'bg-gray-200'}`} />
        <div className={`h-1 flex-1 rounded ${strength === 'strong' ? strengthColors.strong : 'bg-gray-200'}`} />
      </div>

      {/* Strength label */}
      <p className={`text-xs font-medium ${strengthTextColors[strength]}`}>
        Password strength: {strength.charAt(0).toUpperCase() + strength.slice(1)}
      </p>

      {/* Requirements checklist */}
      <div className="space-y-1 text-xs">
        <RequirementItem met={checks.length} text="At least 8 characters" />
        <RequirementItem met={checks.uppercase} text="One uppercase letter" />
        <RequirementItem met={checks.lowercase} text="One lowercase letter" />
        <RequirementItem met={checks.number} text="One number" />
        <RequirementItem met={checks.special} text="One special character" />
      </div>
    </div>
  );
};

const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
  <div className={`flex items-center gap-1 ${met ? 'text-green-600' : 'text-gray-500'}`}>
    {met ? (
      <Check className="h-3 w-3" />
    ) : (
      <X className="h-3 w-3" />
    )}
    <span>{text}</span>
  </div>
);
